import type { ContinuityObjectState, PhysicalObjectOperationalStatus } from '../types';
import { normalizeContinuityObjectState } from './physicalContinuity.service';
import type { ArchivistPhysicalCandidateManifestEntry } from './physicalContinuityExtraction.service';

export type PhysicalObjectNormalizationStage =
  | 'RAW_TRANSPORT'
  | 'PARSED_RESPONSE'
  | 'BOUNDARY_NORMALIZED'
  | 'STATE_NORMALIZED';

export interface PhysicalObjectNormalizationAudit {
  stage: PhysicalObjectNormalizationStage;
  itemIndex: number;
  sourceCandidateId?: string;
  originalKeySet: string[];
  normalizedKeySet: string[];
  rejectionCode?: string;
}

const OPERATIONAL_FROM_STATE: Record<string, PhysicalObjectOperationalStatus> = {
  DISABLED: 'DISABLED',
  DAMAGED: 'DAMAGED',
  DESTROYED: 'DESTROYED',
  OPERATIONAL: 'OPERATIONAL',
  ABANDONED: 'ABANDONED',
  MISSING: 'MISSING',
  IMMOBILE: 'DISABLED',
};

function readString(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed || null;
}

function readCandidateId(record: Record<string, unknown>): string | null {
  return (
    readString(record.source_candidate_id) ||
    readString(record.sourceCandidateId) ||
    readString(record.candidateId)
  );
}

export function diagnosePhysicalObjectEntry(
  raw: unknown,
  itemIndex: number,
  stage: PhysicalObjectNormalizationStage = 'PARSED_RESPONSE',
): PhysicalObjectNormalizationAudit {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return {
      stage,
      itemIndex,
      originalKeySet: [],
      normalizedKeySet: [],
      rejectionCode: 'INVALID_ENTRY',
    };
  }
  const record = raw as Record<string, unknown>;
  const originalKeySet = Object.keys(record).sort();
  if (originalKeySet.length === 0) {
    return {
      stage,
      itemIndex,
      originalKeySet,
      normalizedKeySet: [],
      rejectionCode: 'EMPTY_PHYSICAL_OBJECT',
    };
  }
  const sourceCandidateId = readCandidateId(record) || undefined;
  const hasIdentityField =
    Boolean(readString(record.canonicalName) || readString(record.name) || readString(record.objectName)) ||
    Boolean(sourceCandidateId);
  if (!hasIdentityField) {
    const recognized = originalKeySet.filter((key) =>
      ['objectType', 'operationalStatus', 'state', 'mobilityStatus'].includes(key),
    );
    return {
      stage,
      itemIndex,
      sourceCandidateId,
      originalKeySet,
      normalizedKeySet: recognized,
      rejectionCode: recognized.length > 0 ? 'MISSING_IDENTITY_FIELDS' : 'UNRECOGNIZED_OBJECT_KEYS',
    };
  }
  return {
    stage,
    itemIndex,
    sourceCandidateId,
    originalKeySet,
    normalizedKeySet: originalKeySet,
  };
}

export function logPhysicalObjectNormalizationAudit(audit: PhysicalObjectNormalizationAudit): void {
  console.log(`[PHYSICAL OBJECT NORMALIZATION AUDIT] ${JSON.stringify(audit)}`);
}

export function enrichPhysicalObjectEntryFromManifest(
  raw: unknown,
  manifest: ArchivistPhysicalCandidateManifestEntry[],
): Record<string, unknown> | null {
  const audit = diagnosePhysicalObjectEntry(raw, 0);
  if (audit.rejectionCode === 'EMPTY_PHYSICAL_OBJECT' || audit.rejectionCode === 'INVALID_ENTRY') {
    logPhysicalObjectNormalizationAudit(audit);
    return null;
  }
  const record = { ...(raw as Record<string, unknown>) };
  const candidateId = readCandidateId(record);
  const manifestEntry = candidateId
    ? manifest.find((entry) => entry.candidateId === candidateId)
    : undefined;

  if (candidateId && !manifestEntry) {
    logPhysicalObjectNormalizationAudit({
      ...audit,
      sourceCandidateId: candidateId,
      rejectionCode: 'UNKNOWN_CANDIDATE_ID',
    });
  }

  if (manifestEntry) {
    if (!readString(record.canonicalName) && !readString(record.name) && !readString(record.objectName)) {
      record.canonicalName = manifestEntry.canonicalName;
    }
    if (!readString(record.objectType)) {
      record.objectType = manifestEntry.objectType;
    }
    if (!readString(record.continuityImportance)) {
      record.continuityImportance = manifestEntry.continuityImportance;
    }
    if (manifestEntry.signalType === 'DISABLED_VEHICLE' && !readString(record.operationalStatus)) {
      record.operationalStatus = 'DISABLED';
    }
    if (manifestEntry.signalType === 'WEAPON_DESTRUCTION' && !readString(record.operationalStatus)) {
      record.operationalStatus = 'DESTROYED';
    }
    if (!readString(record.mobilityStatus) && manifestEntry.signalType === 'DISABLED_VEHICLE') {
      record.mobilityStatus = 'IMMOBILE';
    }
  }

  const stateText = readString(record.state) || readString(record.condition);
  if (stateText && !readString(record.operationalStatus)) {
    const mapped = OPERATIONAL_FROM_STATE[stateText.toUpperCase()];
    if (mapped) record.operationalStatus = mapped;
  }

  if (!readString(record.canonicalName) && !readString(record.name) && !readString(record.objectName)) {
    logPhysicalObjectNormalizationAudit({
      ...audit,
      sourceCandidateId: candidateId,
      rejectionCode: 'MISSING_CANONICAL_NAME',
    });
    return record;
  }

  return record;
}

export function normalizeIncomingPhysicalObject(
  raw: unknown,
  params: {
    chapterNumber: number;
    itemIndex: number;
    manifest: ArchivistPhysicalCandidateManifestEntry[];
    projectId?: string;
    existingObjects?: ContinuityObjectState[];
    stage?: PhysicalObjectNormalizationStage;
  },
): { object: ContinuityObjectState | null; rejectionCode?: string } {
  const audit = diagnosePhysicalObjectEntry(raw, params.itemIndex, params.stage || 'BOUNDARY_NORMALIZED');
  if (audit.rejectionCode === 'EMPTY_PHYSICAL_OBJECT' || audit.rejectionCode === 'INVALID_ENTRY') {
    logPhysicalObjectNormalizationAudit(audit);
    return { object: null, rejectionCode: audit.rejectionCode };
  }

  const enriched = enrichPhysicalObjectEntryFromManifest(raw, params.manifest);
  if (!enriched) {
    return { object: null, rejectionCode: audit.rejectionCode || 'EMPTY_PHYSICAL_OBJECT' };
  }

  const normalized = normalizeContinuityObjectState(enriched, params.chapterNumber, params.itemIndex, {
    projectId: params.projectId,
    existingObjects: params.existingObjects,
  });

  if (!normalized) {
    const postAudit = diagnosePhysicalObjectEntry(enriched, params.itemIndex, 'STATE_NORMALIZED');
    logPhysicalObjectNormalizationAudit({
      ...postAudit,
      rejectionCode: postAudit.rejectionCode || 'MISSING_CANONICAL_NAME',
    });
    return { object: null, rejectionCode: postAudit.rejectionCode || 'MISSING_CANONICAL_NAME' };
  }

  return { object: normalized };
}
